import { getReferenceData } from 'common/reference-data/containers/reference-data-actions';
import { fetchInstalledAtInfo } from 'installed-at/info/containers/installed-at-info-actions';
import { getOrderDetailsRequest } from 'order/details/containers/details-actions';
import { call, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/api';
import { referenceTypes } from '../../../utils/app-configs';
import * as actions from './info-actions';
import * as types from './info-constants';

export const getServiceCenterId = state => state.navigation.navigationSelf.serviceCenterId;

export const getAllEquipmentPartnerTypes =
  state => state.referenceData.data[`${getServiceCenterId}_equipment_partnerTypes`];
export function* getEquipmentInfo(action) {
  try {
    const { results, requestCancelled } = yield race({
      results: call(callApi, {
        endpoint: `info/equipment/${action.payload.id}`,
        options: {
          errorMessage: `Could not get equipment details ${action.payload.id}`,
          retryAction: action,
        },
      }),
      // Check if user redirects to installedAt info or order details.
      // In that case, stop the equipment info request to prevent
      // wrong data on the installedAt info or order details page
      requestCancelled: take([getOrderDetailsRequest, fetchInstalledAtInfo]),
    });
    if (!requestCancelled) {
      const partnerTypes = yield select(getAllEquipmentPartnerTypes);
      const serviceCenterId = yield select(getServiceCenterId);
      if (!partnerTypes || action?.payload?.shouldRefetchPartners) {
        yield put(
          getReferenceData({
            objectType: referenceTypes.equipment,
            name: 'partnerTypes',
            urlParams: '',
            serviceCenterId,
          }),
        );
      }
      yield put(actions.getEquipmentInfoSuccess(results));
    }
  } catch (err) {
    yield put(actions.getEquipmentInfoFailure(err));
  }
}

export function* saveEquipmentInfo(action) {
  try {
    const mapping = {
      name: 'name',
      mainWorkCenterName: 'mainWorkCenterName',
      mainWorkCenterNumber: 'mainWorkCenterNumber',
      salesOfficeName: 'salesOfficeName',
      salesOfficeNumber: 'salesOfficeNumber',
      salesGroupName: 'salesGroupName',
      salesGroupNumber: 'salesGroupNumber',
      plannerGroupName: 'plannerGroupName',
      plannerGroupNumber: 'plannerGroupNumber',
      prefWorkCenterName: 'prefWorkCenterName',
      prefWorkCenterNumber: 'prefWorkCenterNumber',
      characteristics: 'characteristics',
      validFromDate: 'validFromDate',
      startUpDate: 'startUpDate',
      acquisitionDate: 'acquisitionDate',
      warrStartDateVendor: 'warrStartDateVendor',
      warrEndDateVendor: 'warrEndDateVendor',
      warrStartDateCustomer: 'warrStartDateCustomer',
      warrEndDateCustomer: 'warrEndDateCustomer',
      statuses: 'statuses',
      countryOfEquipment: 'countryOfEquipmentId',
    };
    const { data, values } = action.payload;
    const request = Object.keys(mapping).reduce(
      (acc, key) => {
        const mappedField = mapping[key];
        if (values[key] && key.includes('Date')) {
          acc[mappedField] = values[key].format('MM/DD/YYYY');
        } else {
          acc[mappedField] = values[key];
        }
        return acc;
      },
      { ...data },
    );
    const results = yield call(callApi, {
      endpoint: `info/update/equipment/${data.id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(request),
        errorMessage: 'Could not update equipment',
        retryAction: action,
      },
    });
    yield put(actions.saveEquipmentInfoSuccess(results));
  } catch (err) {
    yield put(actions.saveEquipmentInfoFailure(err));
  }
}

export function* fetchConnectionStatus(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `details/equipment/${action.payload.id}/connectionState`,
      options: {
        errorMessage: `Could not get connection state for equipment ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(actions.fetchConnectionStatusSuccess(results));
  } catch (err) {
    yield put(actions.fetchConnectionStatusFailure(err));
  }
}

function* watchGetEquipmentInfo() {
  yield takeLatest(types.GET_EQUIPMENT_INFO_REQUEST, getEquipmentInfo);
}

function* watchSaveEquipmentInfo() {
  yield takeLatest(types.SAVE_EQUIPMENT_INFO_REQUEST, saveEquipmentInfo);
}

function* watchFetchConnectionStatus() {
  yield takeLatest(types.CONNECTION_STATUS_REQUEST, fetchConnectionStatus);
}

export default [
  fork(watchGetEquipmentInfo),
  fork(watchSaveEquipmentInfo),
  fork(watchFetchConnectionStatus),
];
